import { useState } from "react";
import {
  useListAccounts,
  useMergeAccounts,
  getListAccountsQueryKey,
  getGetAccountQueryKey,
  type Account,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, GitMerge } from "lucide-react";

// Phase 23-07 MERGE-01: Maveltec-style merge wizard (3 steps)
type Step = "pick" | "preview" | "done";

interface Props {
  open: boolean;
  targetAccountId: number;
  onClose: () => void;
}

export function MergeDialog({ open, targetAccountId, onClose }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const mergeAccounts = useMergeAccounts();

  const [step, setStep] = useState<Step>("pick");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: allAccounts, isLoading } = useListAccounts(
    { status: "active", pageSize: 200 },
    { query: { queryKey: ["/api/accounts", { status: "active" }] } }
  );

  // Eligible sources: same space, exclude target, exclude already-merged
  const eligibleSources = (allAccounts ?? []).filter(
    (a: Account) => a.id !== targetAccountId && a.status === "active"
  );

  const targetAccount = (allAccounts ?? []).find((a: Account) => a.id === targetAccountId);
  const selectedAccounts = eligibleSources.filter((a: Account) => selectedIds.has(a.id));

  function toggleSelect(id: number) {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  }

  function handleReset() {
    setStep("pick");
    setSelectedIds(new Set());
    setErrorMessage(null);
  }

  function handleClose() {
    handleReset();
    onClose();
  }

  async function handleMerge() {
    setErrorMessage(null);
    try {
      await mergeAccounts.mutateAsync({
        id: targetAccountId,
        data: { sourceAccountIds: Array.from(selectedIds) },
      });
      toast({
        title: `Merged ${selectedIds.size} account${selectedIds.size > 1 ? "s" : ""} into ${targetAccount?.legalName ?? "target"}.`,
      });
      queryClient.invalidateQueries({ queryKey: getListAccountsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetAccountQueryKey(targetAccountId) });
      setStep("done");
    } catch (err) {
      const message = (err as Error)?.message ?? "Merge failed";
      if (message.includes("422") || message.includes("cross")) {
        setErrorMessage("Cannot merge accounts from different spaces. All accounts must belong to the same space.");
      } else {
        setErrorMessage(`Merge failed: ${message}`);
      }
    }
  }

  const canProceed = selectedIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5" />
            Merge Accounts
          </DialogTitle>
        </DialogHeader>

        {step === "pick" && (
          <>
            {targetAccount && (
              <div className="text-sm text-muted-foreground mb-2">
                Target account:{" "}
                <span className="font-medium text-foreground">{targetAccount.legalName}</span>
                {" "}— selected accounts will be folded into this account.
              </div>
            )}

            {isLoading ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded border">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                ))}
              </div>
            ) : eligibleSources.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No other active accounts available to merge.
              </p>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto border rounded-md p-1">
                {eligibleSources.map((account: Account) => {
                  const isSelected = selectedIds.has(account.id);
                  return (
                    <div
                      key={account.id}
                      className={`flex items-center gap-3 p-2.5 rounded cursor-pointer hover:bg-muted/50 ${isSelected ? "bg-primary/5" : ""}`}
                      onClick={() => toggleSelect(account.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(account.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{account.legalName}</p>
                        <div className="flex gap-1 mt-0.5">
                          <Badge variant="outline" className="text-xs">{account.kind}</Badge>
                          <Badge variant="outline" className="text-xs">{account.type}</Badge>
                          {account.gstin && (
                            <span className="text-xs text-muted-foreground font-mono">{account.gstin}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={() => setStep("preview")} disabled={!canProceed}>
                Next: Preview ({selectedIds.size} selected)
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "preview" && (
          <>
            <div className="space-y-3">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  <strong>This cannot be undone.</strong> The source accounts will be archived with status &quot;merged&quot;.
                </AlertDescription>
              </Alert>

              <div className="text-sm">
                <p className="mb-2">
                  These <strong>{selectedIds.size}</strong> account{selectedIds.size > 1 ? "s" : ""} will be folded into{" "}
                  <strong>{targetAccount?.legalName ?? "target"}</strong>:
                </p>
                <ul className="space-y-1 pl-4">
                  {selectedAccounts.map((a: Account) => (
                    <li key={a.id} className="text-sm text-muted-foreground">
                      • {a.legalName} <span className="text-xs">({a.kind})</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm text-muted-foreground">
                  All contacts from source accounts will be moved to the target account.
                  Source accounts will be archived with status &apos;merged&apos;.
                </p>
              </div>

              {errorMessage && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setStep("pick"); setErrorMessage(null); }}>Back</Button>
              <Button
                variant="destructive"
                onClick={handleMerge}
                disabled={mergeAccounts.isPending}
              >
                {mergeAccounts.isPending ? "Merging…" : "Confirm Merge"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "done" && (
          <>
            <div className="text-center py-6">
              <GitMerge className="h-10 w-10 text-primary mx-auto mb-3" />
              <p className="text-sm font-medium">Merge complete!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Source accounts are now archived. All contacts have been moved to{" "}
                {targetAccount?.legalName ?? "the target account"}.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
